/// <reference types="@emotion/react/types/css-prop" />
import {useAttendee} from '@backstage-components/attendee-container';
import {
  useAnimationStates,
  useShowInstructions,
  type DeriveInstructionType,
} from '@backstage-components/base';
import {Instruction} from '@backstage/instructions';
import {css} from '@emotion/react';
import type {PlayerAPI, SourceConfig} from 'bitmovin-player';
import {isValid, parseISO} from 'date-fns';
import {useSubscription} from 'observable-hooks';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type FC,
  type Reducer,
  type RefObject,
} from 'react';
import {Observable} from 'rxjs';
import {
  isPlayerApiNotAvailable,
  useHandlePlayer,
  usePlayer,
} from './Video.hooks';
import type {
  ControlFlags,
  DrmConfig,
  IWatermarkProps,
  ReloadSourceFn,
  VideoComponentDefinition,
} from './Video.interfaces';
import * as Styled from './Video.styled';
import {
  fetchFairplayCertificate,
  generateWatermark,
  generateWrapperClassName,
  getMappedSource,
  parseIntegerArray,
  queryPlayerSwitch,
} from './Video.utils';
import {ComponentDefinition} from './VideoDefinition';
import {PlayerUI} from './ui';
import {useBitmovin} from './use-bitmovin';

type PlayFn = PlayerAPI['play'];
type PauseFn = PlayerAPI['pause'];

const Watermark: FC<IWatermarkProps> = ({position, text}) => (
  <Styled.VideoWrapper>
    <Styled.RatioWrapper>
      <Styled.WatermarkTextContainer
        position={position}
        className="watermark-text"
      >
        {text}
      </Styled.WatermarkTextContainer>
    </Styled.RatioWrapper>
  </Styled.VideoWrapper>
);

const renderedComponents = (
  definition: VideoComponentDefinition,
  slotName: string
): JSX.Element[] => {
  const {slotRenderer: Component = () => <Fragment />} = definition;
  const slot = definition.slots?.[slotName];
  const components = Array.isArray(slot) ? slot : slot ? [slot] : [];
  return components.map((component) => (
    <Component
      key={component.mid}
      {...component}
      style={`${component.style ?? ''};`}
    />
  ));
};

export const VideoComponent: FC<VideoComponentDefinition> = (definition) => {
  const {
    autoplay = false,
    controlFlags,
    showControls = true,
    colorSeekbarLine,
    colorAccent,
    hasWatermark = false,
    pictureInPicture = false,
    loop,
    source,
    watermarkOptions = {},
    advancedOptions,
    animationStates,
    poster,
  } = definition.props;

  const [atop, between] = useMemo(() => {
    return [
      renderedComponents(definition, 'atop'),
      renderedComponents(definition, 'between'),
    ];
  }, [definition]);

  const {watermarkTemplate = '', watermarkPosition} = watermarkOptions;

  const {
    startTime: rawStartTime,
    useCbls: initalCbls = false,
    forceAutoplay = false,
    markers: timelineMarkers,
    newUi = false,
  } = advancedOptions;

  const attendee = useAttendee();

  const endpointUrl =
    definition.config.scope === 'attendee'
      ? definition.config.attendeeApiEndpoint
      : undefined;

  const sessionToken = attendee?.sessionToken;

  const [isControlsShowing, setIsControlsShowing] =
    useState<boolean>(showControls);
  const [isFixedVideo, setIsFixedVideo] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<Date | undefined>(
    typeof rawStartTime === 'string' ? parseISOValid(rawStartTime) : undefined
  );
  const [useCbls, setUseCbls] = useState<boolean>(initalCbls);
  const generatedWatermark = useMemo(() => {
    return generateWatermark(watermarkTemplate, attendee);
  }, [attendee, watermarkTemplate]);

  const timeServiceUrl =
    definition.config.scope === 'attendee'
      ? definition.config.timestampEndpoint
      : undefined;

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const $player = useRef<HTMLDivElement | null>(null);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);
  const isDrmLicenseLoaded = useRef(false);
  const setDrmLicenseLoaded = useCallback((value: boolean) => {
    isDrmLicenseLoaded.current = value;
  }, []);
  const isSourceLoaded = useRef(false);
  const isDrmEnabled = 'drmEnabled' in source && source.drmEnabled;

  // If our player is in CBLS (cloud based live streaming) mode,
  // we hide the scrub and playpause controls in order to simulate live streaming.
  const computedControlFlags: ControlFlags = useMemo(
    () => ({
      scrub: useCbls ? false : isControlsShowing ?? controlFlags?.scrub,
      playPause: useCbls ? false : isControlsShowing ?? controlFlags?.playPause,
      liveBug: isControlsShowing ?? controlFlags?.liveBug,
      hugePlay: isControlsShowing ?? controlFlags?.hugePlay,
      fullscreen: isControlsShowing ?? controlFlags?.fullscreen,
      buffer: isControlsShowing ?? controlFlags?.buffer,
      volume: isControlsShowing ?? controlFlags?.volume,
      mute: isControlsShowing ?? controlFlags?.mute,
      replay: isControlsShowing ?? controlFlags?.replay,
      settings: isControlsShowing ?? controlFlags?.settings,
      title: isControlsShowing ?? controlFlags?.title,
      audioTracks: isControlsShowing ?? controlFlags?.audioTracks ?? false,
      captions: isControlsShowing ?? controlFlags?.captions ?? false,
    }),
    [useCbls, isControlsShowing, controlFlags]
  );

  const defaultSubtitleLangCode = '';
  const playerResult = usePlayer($player, definition, defaultSubtitleLangCode);
  const player = playerResult?.playerApi;
  const drmToken = playerResult?.drmToken;
  const uiManager = playerResult?.uiManager;

  const drmConfig = useMemo<DrmConfig>(
    () => ({
      token: drmToken,
      config: {
        endpointUrl,
        sessionToken,
        isDrmRequired: `${isDrmEnabled ?? false}`,
        hasServerSideForensic: `${
          'serversideForensic' in source && (source.serversideForensic ?? false)
        }`,
      },
      isDrmRequired: isDrmEnabled ?? false,
    }),
    [drmToken, endpointUrl, isDrmEnabled, sessionToken, source]
  );

  const [{mappedSource}, dispatch] = useReducer(mappedSourceReducer, {
    drmConfig,
    mappedSource: getMappedSource({...source, poster}, drmConfig),
    unmappedSource: {...source, poster},
  });

  // When `drmConfig` changes, update `mappedSource`
  useEffect(() => dispatch({type: 'UpdateDrmConfig', drmConfig}), [drmConfig]);

  // If our player is in CBLS (cloud based live streaming) mode,
  // we hide the scrub and playpause controls in order to simulate live streaming.
  const mappedControlFlags = useMemo(
    () => ({
      ...controlFlags,
      ...computedControlFlags,
    }),
    [computedControlFlags, controlFlags]
  );

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  const motionOptions = useAnimationStates(
    // Type assertion is necessary because of the `unknown` in `:on-metadata`
    // instruction, `useAnimationStates` expects all instructions to be
    // compatible with `JsonObject`
    observable as Observable<Instruction>,
    broadcast,
    animationStates
  );

  useSubscription(observable, (inst): void => {
    switch (inst.type) {
      case 'Video:setStartTime': {
        setStartTime(parseISOValid(inst.meta.startTime));
        break;
      }
      case 'Video:setCbls': {
        setUseCbls(inst.meta.cbls === 'true');
        break;
      }
      case 'Video:toggleControls': {
        setIsControlsShowing(!isControlsShowing);
        break;
      }
      case 'Video:play': {
        play();
        break;
      }
      case 'Video:pause': {
        pause();
        break;
      }
      case 'Video:changeSource': {
        dispatch({type: 'UpdateSource', source: inst.meta});
        break;
      }
      default:
        break;
    }
  });

  const bm = useBitmovin();

  useEffect(() => {
    if (typeof player === 'undefined' || typeof bm === 'undefined') {
      return;
    }
    const {MetadataType, PlayerEvent} = bm;
    player.on(PlayerEvent.Error, (e): void => {
      broadcast({
        type: 'Video:on-error',
        meta: {code: e.code, name: e.name, mesage: e.message},
      });
    });
    player.on(PlayerEvent.Paused, () => {
      broadcast({type: 'Video:on-pause', meta: {}});
    });
    player.on(PlayerEvent.Playing, (): void => {
      broadcast({type: 'Video:on-play', meta: {}});
      broadcast({type: 'Video:started', meta: {}});
    });
    player.on(PlayerEvent.PlaybackFinished, (): void => {
      if (loop) {
        player.play();
      } else {
        broadcast({type: 'Video:on-ended', meta: {}});
        broadcast({type: 'Video:ended', meta: {}});
      }
    });
    player.on(PlayerEvent.Metadata, (event): void => {
      if (event.metadataType === MetadataType.ID3) {
        // HLS metadata is sent as an binary array, so we have to parse it
        // before sending to the broadcaster
        const data =
          'PRIV' in event.metadata &&
          Array.isArray(event.metadata.PRIV) &&
          event.metadata.PRIV.every((v): v is number => typeof v === 'number')
            ? parseIntegerArray(event.metadata.PRIV)
            : event.metadata;

        broadcast({
          type: 'Video:on-metadata',
          meta: {type: event.metadataType, data},
        });
      }
    });
    player.on(PlayerEvent.Muted, () => {
      broadcast({type: 'Video:on-mute', meta: {}});
    });
    player.on(PlayerEvent.SourceLoaded, () => {
      isSourceLoaded.current = true;
    });
    player.on(PlayerEvent.SourceUnloaded, () => {
      isSourceLoaded.current = false;
    });
    player.on(PlayerEvent.Unmuted, () => {
      broadcast({type: 'Video:on-unmute', meta: {}});
    });
  }, [bm, broadcast, definition.cid, loop, player]);

  const play = useCallback<PlayFn>(
    async (issuer) => {
      const isDrmSatisfied = !isDrmEnabled || isDrmLicenseLoaded.current;
      if (isSourceLoaded.current && isDrmSatisfied && !isPlaying(player)) {
        return player?.play(issuer);
      }
    },
    [isDrmEnabled, player]
  );

  const pause = useCallback<PauseFn>(
    async (issuer) => {
      return player?.pause(issuer);
    },
    [player]
  );

  useEffect(() => {
    if (typeof player === 'undefined' || typeof bm === 'undefined') {
      return;
    }
    /** A flag indicating whether the effect has up-to-date values in its closure */
    const autoplayAbort = new AbortController();
    const reloadSource: ReloadSourceFn = async (
      src,
      previousAutoplayAttempted = false
    ) => {
      if (typeof src === 'undefined') return;

      try {
        // If we have FairPlay DRM configured, fetch and inject the server certificate manually
        if (src.drm?.fairplay) {
          src.drm.fairplay.serverCertificate = await fetchFairplayCertificate();
        }

        // if a source has already previously been loaded, we will first unload
        // loaded source
        if (isSourceLoaded.current === true) {
          // unloading a source while a previous player.play() promise call is
          // pending will cause it to reject
          await player.unload();
        }
        // Don't dispatch changes if this effect's destructor has been called
        if (autoplayAbort.signal.aborted) return;
        // Load Player with mappedSource
        await player.load(src);

        // Keep the current playback state when switching source. By default, bitmovin will
        // revert the player to it's initial configuration on source switch. Meaning that if
        // autoplay is disabled, but the user was playing the previous source, the media will
        // pause when the new source is loaded.
        if (isPlaying(player)) {
          await play();
        }

        // this check is to prevent looping with async autoPlay attempts
        if (!previousAutoplayAttempted && !isSafari) {
          player.on(bm.PlayerEvent.Play, () => autoplayAbort.abort());
          await handleAutoplay({
            abortSignal: autoplayAbort.signal,
            isAutoplayAttempted: previousAutoplayAttempted ?? false,
            isAutoplayEnabled: autoplay,
            isAutoplayForced: forceAutoplay,
            isDrmLicenseLoaded,
            player,
            reloadSource: reloadSource.bind(null, src),
          });
        }
      } catch (err) {
        if (!isPlayerApiNotAvailable(err)) {
          console.error('Error while loading source.', err);
        }
      }
    };

    reloadSource(mappedSource);
    return () => {
      autoplayAbort.abort();
    };
    // `player` is needed in the dependencies array of this hook because without
    // it the effect won't re-run after the player instance changes which would
    // leave the source in an unloaded state for the (now set) `player`
  }, [autoplay, bm, forceAutoplay, isSafari, mappedSource, play, player]);

  // Handle Picture in Picture functionality
  // See: https://bitmovin.com/demos/picture-in-picture
  useEffect(() => {
    if (!pictureInPicture) return;

    const adjustPlayer = (): void => {
      const playerWrapper = playerWrapperRef.current;

      if (!playerWrapper) return;
      const playerSwitch = queryPlayerSwitch(playerWrapper);
      if (!playerSwitch) return;

      const lowerEdge = playerWrapper.offsetTop + playerWrapper.offsetHeight;
      const switchToMinPlayerPos = lowerEdge - window.innerHeight / 3;
      const currentScrollPos =
        document.body.scrollTop || document.documentElement.scrollTop;

      if (currentScrollPos > switchToMinPlayerPos) {
        setIsFixedVideo(true);
      } else {
        setIsFixedVideo(false);
      }
    };

    window.addEventListener('scroll', adjustPlayer);
    return () => {
      window.removeEventListener('scroll', adjustPlayer);
    };
  }, [pictureInPicture]);

  useHandlePlayer({
    $player,
    player,
    defaultSubtitleLangCode,
    useCbls,
    setDrmLicenseLoaded,
    startTime,
    uiManager,
    timeServiceUrl,
    timelineMarkers,
  });

  const styles = [
    css({
      width: '100%',
      height: '100%',
      minWidth: '18rem',
      position: 'relative',
    }),
    css`
      ${definition.style}
      ${definition.props.styleAttr}
      &:hover .hover-show {
        visibility: visible;
      }
    `,
  ];

  return (
    <Styled.VideoModuleWrapper
      id={definition.id}
      css={styles}
      {...motionOptions}
      className={generateWrapperClassName(definition.id)}
      ref={playerWrapperRef}
    >
      <Styled.Container
        data-testid="video-container"
        controlFlags={mappedControlFlags}
        colorAccent={colorAccent}
        colorSeekbarLine={colorSeekbarLine}
        className={
          pictureInPicture
            ? `player-switch${isFixedVideo ? ' fixed-player' : ''}`
            : ''
        }
      >
        <Styled.Video ref={$player}>
          {atop.length > 0 && (
            <Styled.Overlay zIndex={10} className="video-atop">
              {atop}
            </Styled.Overlay>
          )}
          {between.length > 0 && (
            <Styled.Overlay zIndex={2} className="video-between">
              {between}
            </Styled.Overlay>
          )}
          {hasWatermark && generatedWatermark && (
            <Watermark text={generatedWatermark} position={watermarkPosition} />
          )}
          {newUi && <PlayerUI player={player} accentColor={colorAccent} />}
        </Styled.Video>
        <Styled.LiveBug />
      </Styled.Container>
    </Styled.VideoModuleWrapper>
  );
};

function parseISOValid(input: string): Date | undefined {
  const result = parseISO(input);
  if (isValid(result)) {
    return result;
  } else {
    return undefined;
  }
}

interface HandleAutoplayOptions {
  abortSignal: AbortSignal;
  isAutoplayAttempted: boolean;
  isAutoplayEnabled: boolean;
  isAutoplayForced: boolean;
  isDrmLicenseLoaded: RefObject<boolean>;
  player: PlayerAPI;
  reloadSource: (hasAttemptedAutoplay?: boolean) => Promise<void>;
}

const handleAutoplay = async (
  options: HandleAutoplayOptions
): Promise<void> => {
  const {
    abortSignal,
    isAutoplayAttempted,
    isAutoplayEnabled,
    isAutoplayForced,
    isDrmLicenseLoaded,
    player,
    reloadSource,
  } = options;
  if (!isAutoplayEnabled) return;
  try {
    /* autoPlayAttemptPromise is a parallel promise that will check to see
     * if player is playing after time ms. If not ( 1. if player is hung
     * and did not drm license request or 2. if player couldn't play for
     * external reason (i.e. no user interaction before autoplay))... it
     * will check to see if wasn't drmLicense had been loaded, in which
     * case, it will attempt to load the source again. otherwise, if
     * drmLicense had been successfully loaded, the player simply could not
     * run autoplay for non-bitmovin related reasons
     */
    const autoPlayAttemptPromise = new Promise((resolve, reject) => {
      const time = 4000;
      setTimeout(() => {
        if (abortSignal.aborted) {
          // Autoplay attempt was aborted do nothing
          resolve('Aborted...');
        } else if (isPlaying(player)) {
          // Autoplay started successfully and we don't need to do anything.
          resolve('Playing...');
        } else {
          if (!isDrmLicenseLoaded.current) {
            try {
              // Attempt to pause player before re-loading source
              player.pause();
            } catch (reason) {
              if (!isPlayerApiNotAvailable(reason)) {
                throw reason;
              }
            }
            // Reload Source
            reloadSource(true);
            reject(new Error('Force timed out... attempting to reload'));
          }
          reject(new Error("Couldn't Autoplay..."));
        }
      }, time);
    });
    await Promise.race([player.play('handleAutoPlay'), autoPlayAttemptPromise]);
  } catch (err) {
    // If we have already attempted to autoplay, or forceAutoplay is false, show a play button
    // If this was the first attempt, and forceAutoplay is true, mute the player and try again
    if (isAutoplayAttempted || !isAutoplayForced) {
      console.error('Attempted to autoplay - no empty block here: ', err);
    } else if (!isAutoplayAttempted && isAutoplayForced) {
      player.mute();
      player.play();
    }
  }
};

/**
 * Test a given `PlayerAPI` to see if it is currently playing handling any
 * `PlayerError` that might be thrown (and treating that as not playing).
 */
function isPlaying(player?: PlayerAPI): boolean {
  let isPlaying = false;
  try {
    isPlaying = player?.isPlaying() ?? false;
  } catch (reason) {
    if (!isPlayerApiNotAvailable(reason)) {
      throw reason;
    }
  }
  return isPlaying;
}

type Source = Extract<
  DeriveInstructionType<(typeof ComponentDefinition)['instructions']>,
  {type: 'Video:changeSource'}
>['meta'];

interface MappedSourceState {
  drmConfig: DrmConfig;
  mappedSource?: SourceConfig;
  unmappedSource: SourceConfig;
}

type MappedSourceAction =
  | {type: 'UpdateSource'; source: Source}
  | {type: 'UpdateDrmConfig'; drmConfig: DrmConfig};

/**
 * Collect the initial `SourceConfig` from the `VideoComponentDefinition` along
 * with the `DrmConfig` and the `SourceConfig` resulting from combining them
 * into a state object. The state object can be modified by either updating the
 * base source or by updating the `DrmConfig`
 */
const mappedSourceReducer: Reducer<MappedSourceState, MappedSourceAction> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'UpdateSource':
      return {
        drmConfig: draft.drmConfig,
        mappedSource: getMappedSource(action.source, draft.drmConfig),
        unmappedSource: action.source,
      };
    case 'UpdateDrmConfig':
      return {
        drmConfig: action.drmConfig,
        mappedSource: getMappedSource(draft.unmappedSource, action.drmConfig),
        unmappedSource: draft.unmappedSource,
      };
    default:
      return draft;
  }
};
